import { useCallback } from 'react';

/**
 * Hook to handle all bulk action operations
 */
export const useBulkActions = ({
  selectedTaskIds,
  onStatusChange,
  onPriorityChange,
  onIconChange,
  onSetPrimaryTag,
  onAddTag,
  onRemoveTag,
  onArchiveTasks,
  onUnarchiveTask,
  onDueDateChange,
  onEstimatedDurationMinutesChange,
  onStartDateChange,
  onCustomFieldValueChange,
  onClearSelection,
  setActiveDropdown,
  setShowIconPicker,
}) => {
  const handleStatusChange = useCallback((statusId) => {
    selectedTaskIds.forEach(taskId => {
      onStatusChange?.(taskId, statusId);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onStatusChange, setActiveDropdown]);

  const handlePriorityChange = useCallback((priorityId) => {
    selectedTaskIds.forEach(taskId => {
      onPriorityChange?.(taskId, priorityId);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onPriorityChange, setActiveDropdown]);

  const handleBulkIconChange = useCallback((icon) => {
    selectedTaskIds.forEach(taskId => {
      onIconChange?.(taskId, icon);
    });
    setShowIconPicker(false);
  }, [selectedTaskIds, onIconChange, setShowIconPicker]);

  const handlePrimaryTagChange = useCallback((tagId) => {
    selectedTaskIds.forEach(taskId => {
      onSetPrimaryTag?.(taskId, tagId);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onSetPrimaryTag, setActiveDropdown]);

  // Batch add a tag to all selected tasks (skips duplicates automatically via onAddTag)
  const handleBulkAddTag = useCallback((tagName) => {
    selectedTaskIds.forEach(taskId => {
      onAddTag?.(taskId, tagName);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onAddTag, setActiveDropdown]);

  // Batch remove a tag from all selected tasks
  const handleBulkRemoveTag = useCallback((tagName) => {
    selectedTaskIds.forEach(taskId => {
      onRemoveTag?.(taskId, tagName);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onRemoveTag, setActiveDropdown]);

  // Clear all tags from all selected tasks
  const handleBulkClearTags = useCallback((tasks) => {
    // For each selected task, remove all its tags
    selectedTaskIds.forEach(taskId => {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        const taskTags = Array.isArray(task.tags) ? task.tags : (task.category ? [task.category] : []);
        taskTags.forEach(tagName => {
          onRemoveTag?.(taskId, tagName);
        });
      }
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onRemoveTag, setActiveDropdown]);

  const handleArchive = useCallback(() => {
    onArchiveTasks?.(selectedTaskIds);
    onClearSelection?.();
  }, [selectedTaskIds, onArchiveTasks, onClearSelection]);

  const handleUnarchive = useCallback(() => {
    selectedTaskIds.forEach(taskId => {
      onUnarchiveTask?.(taskId);
    });
    onClearSelection?.();
  }, [selectedTaskIds, onUnarchiveTask, onClearSelection]);

  const handleDueDateChange = useCallback((date) => {
    selectedTaskIds.forEach(taskId => {
      onDueDateChange?.(taskId, date);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onDueDateChange, setActiveDropdown]);

  const handleEstimatedDurationMinutesChange = useCallback((minutes) => {
    selectedTaskIds.forEach(taskId => {
      onEstimatedDurationMinutesChange?.(taskId, minutes);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onEstimatedDurationMinutesChange, setActiveDropdown]);

  const handleStartDateChange = useCallback((date) => {
    selectedTaskIds.forEach(taskId => {
      onStartDateChange?.(taskId, date);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onStartDateChange, setActiveDropdown]);

  // Bulk update a custom field value on all selected tasks
  const handleBulkCustomFieldChange = useCallback((fieldId, value) => {
    selectedTaskIds.forEach(taskId => {
      onCustomFieldValueChange?.(taskId, fieldId, value);
    });
    setActiveDropdown(null);
  }, [selectedTaskIds, onCustomFieldValueChange, setActiveDropdown]);

  return {
    handleStatusChange,
    handlePriorityChange,
    handleBulkIconChange,
    handlePrimaryTagChange,
    handleBulkAddTag,
    handleBulkRemoveTag,
    handleBulkClearTags,
    handleArchive,
    handleUnarchive,
    handleDueDateChange,
    handleEstimatedDurationMinutesChange: handleEstimatedDurationMinutesChange,
    handleStartDateChange,
    handleBulkCustomFieldChange,
  };
};
